import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Calendar, Download, Filter, PieChart, BarChart3, FileText, ChevronDown, ArrowUpRight, ArrowDownRight, Wallet, CreditCard, Plus, X, Loader2, Tags, FolderPlus } from 'lucide-react';
import api, { financialAPI, projectsAPI } from '../services/api';
import Projects from './Projects';
import { BackHeader } from '../components/BackHeader';

interface FinancialItem {
  category: string;
  amount: number;
  percentage?: number;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
}

interface Report {
  income: FinancialItem[];
  expenses: FinancialItem[];
  totalIncome: number;
  totalExpenses: number;
  balance: number;
  previousBalance: number;
  period: string;
}

// Dead mock generateReport function removed — page uses real financialAPI.getReports

export default function FinancialReports() {
  const [activeTab, setActiveTab] = useState<'overview' | 'income' | 'expenses' | 'history' | 'register-payment' | 'categories' | 'projects'>('overview');
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return now.toISOString().slice(0, 7);
  });
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);
  // F24: Add income entry state
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [savingIncome, setSavingIncome] = useState(false);
  const [incomeForm, setIncomeForm] = useState({
    description: '',
    amount: '',
    category: 'monthly',
    unit_id: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [incomeError, setIncomeError] = useState('');
  // F39: Register manual payment state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [members, setMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    unit_id: '',
    amount: '',
    payment_method: 'efectivo',
    reference: '',
    date: new Date().toISOString().slice(0, 10),
    description: '',
  });
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    setLoading(true);
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const condoId = userData.condoId;
    if (!condoId) {
      setReport(null);
      setLoading(false);
      return;
    }
    financialAPI.getReports(condoId)
      .then((res: any) => {
        // API returns { success: true, data: { income, expenses, totalIncome, ... } }
        // Axios wraps that in res.data, so the inner payload is res.data.data
        const payload = res?.data?.data ?? res?.data ?? res;
        // Guard: ensure income/expenses are arrays before rendering
        if (payload && Array.isArray(payload.income) && Array.isArray(payload.expenses)) {
          setReport(payload);
        } else {
          console.error('Financial reports: unexpected response shape', res);
          setReport(null);
        }
      })
      .catch((err: any) => {
        console.error('Error loading report:', err);
        setReport(null);
      })
      .finally(() => setLoading(false));
  }, [selectedPeriod]);

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    setShowPeriodSelector(false);
  };

  // Demo fix (2026-11-04): the Exportar button was a hardcoded `disabled`
  // placeholder ("Función en desarrollo"), so admins could not export the
  // financial report for the v1 presentation. Client-side CSV export of the
  // currently loaded report (income + expenses + totals), no backend change.
  const handleExportCsv = () => {
    if (!report) return;
    const esc = (v: string | number) => {
      const s = String(v ?? '');
      return /[",\n;]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const money = (n: number) => (Number.isFinite(n) ? n.toFixed(2) : '0.00');
    const rows: string[] = [];
    rows.push('Seccion,Concepto,Categoria,Monto (MXN),Porcentaje');
    report.income.forEach((i) => rows.push(['Ingresos', esc(i.category), '', money(i.amount), i.percentage != null ? `${i.percentage}%` : ''].join(',')));
    report.expenses.forEach((e) => rows.push(['Egresos', esc(e.category), '', money(e.amount), e.percentage != null ? `${e.percentage}%` : ''].join(',')));
    rows.push(['Totales', 'Ingresos totales', '', money(report.totalIncome), ''].join(','));
    rows.push(['Totales', 'Egresos totales', '', money(report.totalExpenses), ''].join(','));
    rows.push(['Totales', 'Balance del periodo', '', money(report.balance), ''].join(','));
    rows.push(['Totales', 'Balance previo', '', money(report.previousBalance), ''].join(','));
    const csv = '\ufeff' + rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte-financiero-${report.period || selectedPeriod}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-full">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#36CCD8]" />
    </div>
  );

  const condoId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').condoId; }
    catch { return undefined; }
  })();

  if (!condoId) return (
    <div className="flex items-center justify-center min-h-full">
      <div className="text-center">
        <div className="w-16 h-16 bg-[var(--v-surface-2)] rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Wallet className="w-8 h-8 text-[var(--v-text-tertiary)]" />
        </div>
        <h2 className="text-xl font-bold text-[var(--v-text-primary)] mb-2">Sin condominio asignado</h2>
        <p className="text-[var(--v-text-secondary)]">Aún no se te ha asignado un condominio. Contacta al administrador para continuar.</p>
      </div>
    </div>
  );

  if (!report) return (
    <div className="flex items-center justify-center min-h-full">
      <div className="text-center">
        <FileText className="w-12 h-12 text-[var(--v-text-tertiary)] mx-auto mb-4" />
        <p className="text-[var(--v-text-secondary)]">No se pudieron cargar los reportes financieros.</p>
        <p className="text-[var(--v-text-tertiary)] text-sm mt-1">Intenta de nuevo más tarde.</p>
      </div>
    </div>
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'up':
        return <ArrowUpRight className="w-4 h-4 text-emerald-500" />;
      case 'down':
        return <ArrowDownRight className="w-4 h-4 text-red-500" />;
      default:
        return <div className="w-4 h-4 rounded-full bg-[var(--v-text-tertiary)]" />;
    }
  };

  const getTrendClass = (trend?: string, isIncome: boolean = false) => {
    if (trend === 'neutral') return 'text-[var(--v-text-secondary)]';
    // For income, up is good (green), down is bad (red)
    // For expenses, down is good (green), up is bad (red)
    if (isIncome) {
      return trend === 'up' ? 'text-emerald-600' : 'text-red-600';
    }
    return trend === 'down' ? 'text-emerald-600' : 'text-red-600';
  };


  // QA fix 2026-10-13: % indicator was rendered as a bare "%" whenever
  // change was missing (backend sends trend neutral + no change). Show the
  // period-over-period change when available, else the category share of
  // the panel total; render nothing when neither is computable.
  const getItemPct = (item: { change?: number; amount: number }, total: number) => {
    if (typeof item.change === 'number') {
      return <span>{item.change > 0 ? '+' : ''}{item.change.toFixed(1)}%</span>;
    }
    if (total > 0 && item.amount > 0) {
      return <span>{((item.amount / total) * 100).toFixed(1)}%</span>;
    }
    return null;
  };
  // F24: Handle add income submission
  const handleAddIncome = async (e: React.FormEvent) => {
    e.preventDefault();
    setIncomeError('');
    setSavingIncome(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const cid = userData.condoId;
      if (!cid) {
        setIncomeError('Sin condominio asignado.');
        setSavingIncome(false);
        return;
      }
      if (!incomeForm.unit_id || !Number.isFinite(Number(incomeForm.amount)) || Number(incomeForm.amount) <= 0) {
        setIncomeError('Selecciona una unidad e indica un monto mayor que cero.');
        return;
      }
      await financialAPI.addIncome(cid, {
        unit_id: incomeForm.unit_id,
        charge_type: incomeForm.category,
        description: incomeForm.description,
        amount: Number(incomeForm.amount),
        currency: 'MXN',
        due_date: incomeForm.date,
      });
      setIncomeForm({ description: '', amount: '', category: 'monthly', unit_id: '', date: new Date().toISOString().slice(0, 10) });
      setShowIncomeModal(false);
      // Refresh
      setLoading(true);
      financialAPI.getReports(cid)
        .then((res: any) => {
          const payload = res?.data?.data ?? res?.data ?? res;
          if (payload && Array.isArray(payload.income) && Array.isArray(payload.expenses)) {
            setReport(payload);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al registrar ingreso';
      setIncomeError(typeof msg === 'string' ? msg : 'Error al registrar ingreso');
    } finally {
      setSavingIncome(false);
    }
  };

  // F39: Load condo members for the unit dropdown
  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) { setMembers([]); return; }
      // Try the members endpoint
      const res = await api.get(`/condos/${condoId}/members`);
      const data = res?.data?.data ?? res?.data ?? [];
      const list = Array.isArray(data) ? data : (data?.members || []);
      setMembers(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Error loading members:', err);
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  // F39: Handle form submission for manual payment registration
  const handleRegisterPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setPaymentError('');
    setSavingPayment(true);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) {
        setPaymentError('Sin condominio asignado.');
        setSavingPayment(false);
        return;
      }
      await financialAPI.registerPayment(condoId, {
        unit_id: paymentForm.unit_id,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference || undefined,
        date: paymentForm.date,
        description: paymentForm.description || undefined,
      });
      // Reset form & close modal
      setPaymentForm({ unit_id: '', amount: '', payment_method: 'efectivo', reference: '', date: new Date().toISOString().slice(0, 10), description: '' });
      setShowPaymentModal(false);
      // Refresh report data
      setLoading(true);
      financialAPI.getReports(condoId)
        .then((res: any) => {
          const payload = res?.data?.data ?? res?.data ?? res;
          if (payload && Array.isArray(payload.income) && Array.isArray(payload.expenses)) {
            setReport(payload);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || 'Error al registrar el pago';
      setPaymentError(typeof msg === 'string' ? msg : 'Error al registrar el pago');
    } finally {
      setSavingPayment(false);
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback="/" />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Reportes Financieros</h2>
          <p className="text-[var(--v-text-secondary)] mt-1">Gestión de ingresos y gastos de la comunidad</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setShowPaymentModal(true);
              loadMembers();
            }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all hover:shadow-lg hover:shadow-emerald-600/25"
          >
            <Plus className="w-5 h-5" />
            Registrar Pago
          </button>
          <div className="relative">
            <button 
              onClick={() => setShowPeriodSelector(!showPeriodSelector)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--v-surface-1)] border border-[var(--v-border-light)] rounded-lg text-sm font-medium text-[var(--v-text-primary)] hover:bg-[var(--v-surface-2)] transition-colors"
            >
              <Calendar className="w-4 h-4" />
              {selectedPeriod}
              <ChevronDown className={`w-4 h-4 transition-transform ${showPeriodSelector ? 'rotate-180' : ''}`} />
            </button>
            {showPeriodSelector && (
              <div className="absolute top-full right-0 mt-2 w-48 bg-[var(--v-surface-1)] rounded-xl shadow-lg border border-[var(--v-border-light)] py-2 z-10">
                <button onClick={() => handlePeriodChange('2026-03')} className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--v-surface-2)]">Marzo 2026</button>
                <button onClick={() => handlePeriodChange('2026-02')} className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--v-surface-2)]">Febrero 2026</button>
                <button onClick={() => handlePeriodChange('2026-01')} className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--v-surface-2)]">Enero 2026</button>
                <button onClick={() => handlePeriodChange('2025-12')} className="w-full px-4 py-2 text-left text-sm hover:bg-[var(--v-surface-2)]">Diciembre 2025</button>
              </div>
            )}
          </div>
          <button 
            onClick={handleExportCsv}
            disabled={!report}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[var(--v-surface-1)] hover:bg-[var(--v-surface-2)] border border-[var(--v-border-light)] rounded-lg text-sm font-medium text-[var(--v-text-primary)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={report ? 'Exportar reporte financiero (CSV)' : 'Sin datos para exportar'}
          >
            <Download className="w-4 h-4" />
            Exportar
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            {report.totalIncome > 0 && <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">+5.2%</span>}
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{formatCurrency(report.totalIncome)}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Ingresos Totales</p>
        </div>

        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            {report.totalExpenses > 0 && <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-full">+2.1%</span>}
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{formatCurrency(report.totalExpenses)}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Gastos Totales</p>
        </div>

        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${report.balance >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
              <DollarSign className={`w-5 h-5 ${report.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
            </div>
          </div>
          <p className={`text-2xl font-bold ${report.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(report.balance)}
          </p>
          <p className="text-[var(--v-text-secondary)] text-sm">Balance del Mes</p>
        </div>

        <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-border-light)] shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Wallet className="w-5 h-5 text-blue-600" />
            </div>
            {(report.previousBalance + report.balance) !== 0 && <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">+12.5%</span>}
          </div>
          <p className="text-2xl font-bold text-[var(--v-text-primary)]">{formatCurrency(report.previousBalance + report.balance)}</p>
          <p className="text-[var(--v-text-secondary)] text-sm">Saldo Acumulado</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--v-border-light)]">
        {[
          { id: 'overview', label: 'Resumen', icon: PieChart },
          { id: 'income', label: 'Ingresos', icon: TrendingUp },
          { id: 'expenses', label: 'Gastos', icon: TrendingDown },
          { id: 'history', label: 'Historial', icon: BarChart3 },
          { id: 'categories', label: 'Categorías', icon: Tags },
          { id: 'projects', label: 'Proyectos', icon: FolderPlus },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-6 py-3 font-medium text-sm transition-colors border-b-2 ${
              activeTab === tab.id
                ? 'text-blue-600 border-blue-600'
                : 'text-[var(--v-text-secondary)] border-transparent hover:text-[var(--v-text-primary)]'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Income Breakdown */}
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
            <div className="p-5 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[var(--v-text-primary)]">Ingresos por Categoría</h3>
                <p className="text-sm text-[var(--v-text-secondary)]">{formatCurrency(report.totalIncome)} total</p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <div className="divide-y divide-[var(--v-border-light)]">
              {report.income && report.income.length > 0 ? report.income.map((item, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-[var(--v-surface-2)] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full bg-emerald-500`} />
                    <span className="text-[var(--v-text-primary)]">{item.category}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[var(--v-text-primary)]">{formatCurrency(item.amount)}</p>
                    <div className={`flex items-center gap-1 text-xs ${getTrendClass(item.trend, true)}`}>
                      {getTrendIcon(item.trend)}
                      {getItemPct(item, report.totalIncome)}
                    </div>
                  </div>
                </div>
              )) : null}
            </div>
          </div>

          {/* Expenses Breakdown */}
          <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
            <div className="p-5 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-[var(--v-text-primary)]">Gastos por Categoría</h3>
                <p className="text-sm text-[var(--v-text-secondary)]">{formatCurrency(report.totalExpenses)} total</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <div className="divide-y divide-[var(--v-border-light)]">
              {report.expenses && report.expenses.length > 0 ? report.expenses.map((item, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between hover:bg-[var(--v-surface-2)] transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full bg-red-500`} />
                    <span className="text-[var(--v-text-primary)]">{item.category}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[var(--v-text-primary)]">{formatCurrency(item.amount)}</p>
                    <div className={`flex items-center gap-1 text-xs ${getTrendClass(item.trend, false)}`}>
                      {getTrendIcon(item.trend)}
                      {getItemPct(item, report.totalExpenses)}
                    </div>
                  </div>
                </div>
              )) : null}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'income' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          <div className="p-5 border-b border-[var(--v-border-light)] flex items-center justify-between">
            <h3 className="font-semibold text-[var(--v-text-primary)]">Detalle de Ingresos</h3>
            <button
              onClick={() => setShowIncomeModal(true)}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Registrar Ingreso
            </button>
          </div>
          <table className="w-full">
            <thead className="bg-[var(--v-surface-2)]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Categoría</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Monto</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">% del Total</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Tendencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--v-border-light)]">
              {report.income && report.income.length > 0 ? report.income.map((item, idx) => {
                const percentage = report.totalIncome > 0 ? (item.amount / report.totalIncome) * 100 : 0;
                return (
                  <tr key={idx} className="hover:bg-[var(--v-surface-2)]">
                    <td className="px-6 py-4 text-[var(--v-text-primary)]">{item.category}</td>
                    <td className="px-6 py-4 text-right font-semibold text-[var(--v-text-primary)]">{formatCurrency(item.amount)}</td>
                    <td className="px-6 py-4 text-right text-[var(--v-text-secondary)]">{percentage.toFixed(1)}%</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`inline-flex items-center gap-1 text-sm ${getTrendClass(item.trend, true)}`}>
                        {getTrendIcon(item.trend)}
                        {item.change && `${item.change > 0 ? '+' : ''}${item.change.toFixed(1)}%`}
                      </span>
                    </td>
                  </tr>
                );
              }) : null}
            </tbody>
            <tfoot className="bg-[var(--v-surface-2)] font-semibold">
              <tr>
                <td className="px-6 py-4 text-[var(--v-text-primary)]">Total Ingresos</td>
                <td className="px-6 py-4 text-right text-emerald-600">{formatCurrency(report.totalIncome)}</td>
                <td className="px-6 py-4 text-right">100%</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {activeTab === 'expenses' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm overflow-hidden">
          <div className="p-5 border-b border-[var(--v-border-light)]">
            <h3 className="font-semibold text-[var(--v-text-primary)]">Detalle de Gastos</h3>
          </div>
          <table className="w-full">
            <thead className="bg-[var(--v-surface-2)]">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Categoría</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Monto</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">% del Total</th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-[var(--v-text-secondary)] uppercase">Tendencia</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--v-border-light)]">
              {report.expenses && report.expenses.length > 0 ? report.expenses.map((item, idx) => {
                const percentage = report.totalExpenses > 0 ? (item.amount / report.totalExpenses) * 100 : 0;
                return (
                  <tr key={idx} className="hover:bg-[var(--v-surface-2)]">
                    <td className="px-6 py-4 text-[var(--v-text-primary)]">{item.category}</td>
                    <td className="px-6 py-4 text-right font-semibold text-[var(--v-text-primary)]">{formatCurrency(item.amount)}</td>
                    <td className="px-6 py-4 text-right text-[var(--v-text-secondary)]">{percentage.toFixed(1)}%</td>
                    <td className="px-6 py-4 text-right">
                      <span className={`inline-flex items-center gap-1 text-sm ${getTrendClass(item.trend, false)}`}>
                        {getTrendIcon(item.trend)}
                        {item.change && `${item.change > 0 ? '+' : ''}${item.change.toFixed(1)}%`}
                      </span>
                    </td>
                  </tr>
                );
              }) : null}
            </tbody>
            <tfoot className="bg-[var(--v-surface-2)] font-semibold">
              <tr>
                <td className="px-6 py-4 text-[var(--v-text-primary)]">Total Gastos</td>
                <td className="px-6 py-4 text-right text-red-600">{formatCurrency(report.totalExpenses)}</td>
                <td className="px-6 py-4 text-right">100%</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm p-12 text-center">
          <BarChart3 className="w-16 h-16 text-[var(--v-text-tertiary)] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[var(--v-text-primary)] mb-2">Historial en desarrollo</h3>
          <p className="text-[var(--v-text-secondary)]">Próximamente podrás ver comparativas mensuales y anuales.</p>
        </div>
      )}

      {activeTab === 'categories' && (
        <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-border-light)] shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Tags className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-semibold text-[var(--v-text-primary)]">Categorías Financieras</h3>
              <p className="text-sm text-[var(--v-text-secondary)]">Gestiona las categorías de ingresos y gastos</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-[var(--v-text-primary)] mb-3">Categorías de Ingresos</h4>
              <div className="space-y-2">
                {(report.income || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2 bg-emerald-50 rounded-lg">
                    <span className="text-[var(--v-text-primary)] text-sm">{item.category}</span>
                    <span className="text-emerald-600 text-sm font-medium">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                {(!report.income || report.income.length === 0) && (
                  <p className="text-[var(--v-text-tertiary)] text-sm text-center py-4">Sin categorías de ingreso</p>
                )}
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-[var(--v-text-primary)] mb-3">Categorías de Gastos</h4>
              <div className="space-y-2">
                {(report.expenses || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between px-3 py-2 bg-red-50 rounded-lg">
                    <span className="text-[var(--v-text-primary)] text-sm">{item.category}</span>
                    <span className="text-red-600 text-sm font-medium">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
                {(!report.expenses || report.expenses.length === 0) && (
                  <p className="text-[var(--v-text-tertiary)] text-sm text-center py-4">Sin categorías de gasto</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'projects' && (
        <Projects condoId={condoId!} />
      )}

      {/* F24: Add Income Modal */}
      {showIncomeModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Registrar Ingreso</h3>
              <button
                onClick={() => setShowIncomeModal(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddIncome} className="p-6 space-y-4">
              {incomeError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
                  {incomeError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción *</label>
                <input
                  type="text"
                  value={incomeForm.description}
                  onChange={(e) => setIncomeForm({ ...incomeForm, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Ej: Cuota mensual Unidad 12"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Monto *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={incomeForm.amount}
                  onChange={(e) => setIncomeForm({ ...incomeForm, amount: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Categoría *</label>
                <select value={incomeForm.category} onChange={(e) => setIncomeForm({ ...incomeForm, category: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg">
                  <option value="monthly">Cuota mensual</option><option value="rent">Renta</option><option value="amenity">Amenidad</option><option value="other">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Unidad *</label>
                <select required value={incomeForm.unit_id} onFocus={loadMembers} onChange={(e) => setIncomeForm({ ...incomeForm, unit_id: e.target.value })} className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg">
                  <option value="">Selecciona una unidad</option>
                  {members.map((member: any) => <option key={member.unit_id || member.id} value={member.unit_id || member.id}>{member.unit_label || member.unit || member.name || member.id}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fecha *</label>
                <input
                  type="date"
                  value={incomeForm.date}
                  onChange={(e) => setIncomeForm({ ...incomeForm, date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowIncomeModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingIncome}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {savingIncome ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : (
                    'Registrar Ingreso'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* F39: Register Manual Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--v-surface-1)] rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-[var(--v-border-light)] flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[var(--v-text-primary)]">Registrar Pago Manual</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="p-2 text-[var(--v-text-tertiary)] hover:text-[var(--v-text-secondary)] hover:bg-[var(--v-surface-2)] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleRegisterPayment} className="p-6 space-y-4">
              {paymentError && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
                  {paymentError}
                </div>
              )}
              {/* Unidad dropdown */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Unidad *</label>
                {membersLoading ? (
                  <div className="flex items-center gap-2 text-[var(--v-text-secondary)] text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Cargando residentes...
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-[var(--v-text-secondary)] text-sm">No hay residentes registrados.</p>
                ) : (
                  <select
                    value={paymentForm.unit_id}
                    onChange={(e) => setPaymentForm({ ...paymentForm, unit_id: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecciona una unidad...</option>
                    {members.map((m: any) => (
                      <option key={m.user_id || m.id || m.unit_id} value={m.unit_id || m.id}>
                        {m.unit_label || m.unit || m.name || 'Unidad'}{m.name ? ` - ${m.name}` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {/* Monto */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Monto *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>
              {/* Método de Pago */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Método de Pago *</label>
                <select
                  value={paymentForm.payment_method}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="efectivo">Efectivo</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="tarjeta">Tarjeta</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              {/* Referencia */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Referencia (opcional)</label>
                <input
                  type="text"
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Número de referencia o folio"
                />
              </div>
              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Fecha *</label>
                <input
                  type="date"
                  value={paymentForm.date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              {/* Descripción */}
              <div>
                <label className="block text-sm font-medium text-[var(--v-text-primary)] mb-2">Descripción (opcional)</label>
                <textarea
                  value={paymentForm.description}
                  onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2.5 border border-[var(--v-border-light)] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Notas sobre el pago..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-2.5 border border-[var(--v-border-light)] text-[var(--v-text-primary)] rounded-lg font-medium hover:bg-[var(--v-surface-2)] transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingPayment}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {savingPayment ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</>
                  ) : (
                    'Registrar Pago'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
